<template>
  <section
    ref="$block"
    :class="{
      'osk-home-blocks-vertical-collection': true,
    }"
  >
    <div class="osk-wrapper">
      <h2
        v-if="data.title"
        class="osk-home-blocks-vertical-collection__title"
      >
        {{ data.title }}
      </h2>

      <ul class="osk-home-blocks-vertical-collection__list">
        <li
          v-for="(item, index) in items"
          :key="index"
          class="osk-home-blocks-vertical-collection__item"
        >
          <nuxt-link
            class="osk-home-blocks-vertical-collection__card"
            :to="item.link"
            @click="handleClick(item, index)"
          >
            <img
              :src="item.imgPath"
              alt="main-image"
              class="osk-home-blocks-vertical-collection__card-image"
            >

            <div class="osk-home-blocks-vertical-collection__card-content">
              <span class="osk-home-blocks-vertical-collection__card-title">
                {{ item.title }}
              </span>
            </div>
          </nuxt-link>
        </li>
      </ul>

      <ClientOnly>
        <UikitButton
          v-if="showButton"
          class="osk-home-blocks-vertical-collection__button"
          outline
          full-width
          :size="!isMounted || isDesktop ? 's' : 'm'"
          @click="showAll = true"
        >
          {{ $t('common.showMore') }}
        </UikitButton>
      </ClientOnly>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { PrimaryContentDTO, AdditionalCollectionDTO } from '~/restAPI/data-contracts';
import { AnalyticsEvents } from '~/types/analytics';

const props = defineProps<{
  data: PrimaryContentDTO
}>();

const $block = ref<ComponentPublicInstance>();
const showAll = ref(false);

const { isDesktop, isMounted } = useUiBreakpoints();
const { $addEvent } = useNuxtApp();

useScrollEvent($block, event);

const allItems = computed(() => {
  const contentList = (props.data.contentList ?? []) as AdditionalCollectionDTO[];
  const additionalContentList = (props.data.additionalContentList ?? []) as AdditionalCollectionDTO[];

  const result = [
    ...contentList.map((item) => ({
      ...item,
      link: genSPALink(item.link),
    })),
    ...additionalContentList.map((item) => ({
      ...item,
      link: genSPALink(item.link),
    })),
  ];

  return result;
});

const items = computed(() => {
  if (!showAll.value) {
    if (!isMounted.value || isDesktop.value) {
      return allItems.value.slice(0, 8);
    }

    return allItems.value.slice(0, 6);
  }

  return allItems.value;
});

const showButton = computed(() => allItems.value.length !== items.value.length);

function event() {
  $addEvent(AnalyticsEvents.SwiperBlockView, {
    chapter: props.data.contentType,
    chapter_index: props.data.chapterIndex,
    segment_id: props.data.segmentId,
    id_chapter: props.data.id,
  });
}

function handleClick(item: AdditionalCollectionDTO, index: number) {
  const params = {
    chapter: props.data.contentType,
    chapter_index: props.data.chapterIndex,
    segment_id: props.data.segmentId,
    id_chapter: props.data.id,
    id_item: item.id,
    index,
    item: 'banner',
  };

  $addEvent(AnalyticsEvents.SwiperBlockClick, params);
}
</script>

<style scoped lang="scss">
@import "~/assets/scss/settings/index";

.osk-home-blocks-vertical-collection {
  padding-bottom: 42px;

  @include media-query(lg-and-up) {
    padding-bottom: 82px;
  }

  &__title {
    @include overflow-ellipsis;
    @include font-heading;
    padding: 0 0 22px;

    @include media-query(lg-and-up) {
      text-transform: uppercase;
      text-align: center;
      padding-bottom: 34px;
      max-width: 1000px;
      margin: 0 auto;
    }
  }

  &__list {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -6px 12px;

    @include media-query(lg-and-up) {
      margin: 0 -12px -24px;
    }
  }

  &__item {
    padding: 0 6px;
    margin-bottom: 12px;
    width: 50%;
    height: 130px;

    @include media-query(md-and-up) {
      border-radius: 4px;
      height: 232px;
    }

    @include media-query(lg-and-up) {
      height: 252px;
      width: 25%;
      padding: 0 12px;
      margin-bottom: 24px;
    }

    @include media-query(xl-and-up) {
      height: 282px;
    }
  }

  &__card {
    display: flex;
    flex-direction: column;
    position: relative;
    text-align: center;
    height: 100%;
    border-radius: 2px;
    overflow: hidden;

    @include media-query(lg-and-up) {
      border-radius: 4px;
    }
  }

  &__card-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__card-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    position: absolute;
    bottom: 12px;

    @include media-query(lg-and-up) {
      bottom: 24px;
    }
  }

  &__card-title {
    @include line-clamp;
    @include font-style($font-size-body, normal, $font-weight-bold);
    padding: 0 16px;
    text-align: left;

    @include media-query(md-and-up) {
      @include font-style($font-size-subheading, normal, $font-weight-bold);
      padding: 0 24px;
    }
  }

  &__card-text {
    margin-top: $indent-xsmall;

    @include media-query(lg-and-up) {
      margin-top: $indent-compact;
    }
  }

  &__button {
    @include media-query(lg-and-up) {
      width: auto;
      margin: 24px auto 0;
    }
  }
}
</style>
